import React, { useState } from 'react';

import { format } from 'date-fns';
import { ChevronDownIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';
import { isNullOrEmpty } from '@/services/helper';

import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '../ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

type DateControlProps = {
	name: string,
	className?: string,
	label?: string,
	required?: boolean,
	placeholder: string,
	isDisabled?: boolean
} & { calendarProps?: React.ComponentProps<typeof Calendar> };
export default function DateControl({
	name,
	className,
	label,
	placeholder,
	isDisabled,
	calendarProps,
	...props
}: Readonly<DateControlProps>) {
	const [open, setOpen] = useState(false);
	const { control } = useFormContext();

	return (
		<FormField
			control={control}
			name={name}
			render={({
				field: { onChange, value },
				fieldState: { error },
				formState: { disabled }
			}) => (
				<FormItem className={cn('flex w-full flex-col gap-2', className)}>
					{label ? (
						<FormLabel htmlFor={name}>
							{label} {props.required ? <span className="text-red">*</span> : null}
						</FormLabel>
					) : null}
					<FormControl>
						<div className="flex w-full flex-col">
							<Popover open={open} onOpenChange={setOpen} modal>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										aria-expanded={open}
										id="start-date"
										className={cn(
											'border-input/70 hover:border-input hover:text-muted-foreground/80 justify-between overflow-hidden rounded-md bg-transparent text-sm font-normal text-black hover:bg-transparent',
											{
												'text-placeholder': !value,
												'border-destructive border-2': !isNullOrEmpty(error),
												'bg-gray-200': isDisabled || disabled
											}
										)}
										aria-disabled={isDisabled || disabled}
										disabled={isDisabled || disabled}
									>
										{value ? format(value, 'dd MMMM yyyy') : placeholder}
										<ChevronDownIcon className="opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-auto overflow-hidden p-0" align="start">
									<Calendar
										captionLayout="dropdown"
										{...calendarProps}
										mode="single"
										selected={value ?? undefined}
										onSelect={(date) => {
											if (!date) return;

											onChange(date);
											setOpen(false);
										}}
									/>
								</PopoverContent>
							</Popover>
							<FormMessage className="mt-0" />
						</div>
					</FormControl>
				</FormItem>
			)}
		/>
	);
}
